import { ArchiveOutlined, Groups } from "@mui/icons-material";
import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import CRMMenu from "../../../App/Components/Menu/CRMMenu";
import CrmTable from "../../../App/Components/Table/CrmTable";
import { AppFeatureLookup } from "../../../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../../../App/Constants/CrmLiteConstants";
import { useFeaturePermission } from "../../../App/State/Hooks/abacHooks";
import { IGroup } from "../../../App/Typings/Groups";
import { ITablePagination } from "../../../App/Typings/SortableTable";
import { ViewArchivedGroups } from "../ArchivedGroups/ViewArchivedGroups";
import { GroupChip, getRuleFields } from "../DisplayGroupList/DisplayGroupList";

const columns: Column[] = [
  {
    title: "Name",
    key: "name",
    dataType: DataType.String,
  },
  { title: "Type", key: "type", dataType: DataType.String },
  { title: "Candidates", key: "memberCount", dataType: DataType.Number, isSortable: false },
  {
    title: "Last Updated",
    key: "lastUpdateDate",
    dataType: DataType.Date,
  },
  { title: "Created By", key: "creatorNickname", dataType: DataType.String, isSortable: false },
  {
    title: "Created Date",
    key: "createdDate",
    dataType: DataType.Date,
  },
  {
    title: "Rules",
    key: "rules",
    dataType: DataType.String,
    isSortable: false,
  },
  {
    title: "Actions",
    key: "actions",
    dataType: DataType.Object,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
    width: 100,
  },
];

export default function GroupListingTable({
  pagination,
  isLoading,
  groups,
  onGroupArchive,
  sort,
  sortBy,
  onSort,
}: {
  pagination: ITablePagination;
  isLoading: boolean;
  groups: IGroup[];
  onGroupArchive: (groupId: string) => void;
  sort: "asc" | "desc";
  sortBy: string;
  onSort: (prop: string) => void;
}) {
  const accessViewGroup = useFeaturePermission(AppFeatureLookup.GroupsViewGroupProfile);
  const viewGroupMessage = accessViewGroup ? "View Group details" : crmLiteUpgradeMessage;
  const [isArchivedGroupsOpen, setIsArchivedGroupsOpen] = useState(false);

  const navigate = useNavigate();

  const GetGroupActions = (group: IGroup) => {
    return (
      <CRMMenu
        title="Group Actions"
        id="group-action-menu"
        links={[
          {
            dataTestId: "group-view-action",
            title: "View Group",
            tooltip: viewGroupMessage,
            icon: <Groups />,
            isLink: true,
            href: `/candidategroups/${group.id}`,
            disabled: !accessViewGroup,
          },
          {
            dataTestId: "group-archive-action",
            title: "Archive Group",
            tooltip: `Archive ${group.name}`,
            onClick: () => onGroupArchive(group.id),
            icon: <ArchiveOutlined />,
          },
        ]}
      />
    );
  };

  const groupData = useMemo(() => {
    return groups.map((c) => {
      return {
        ...c,
        type: <GroupChip group={c} />,
        actions: GetGroupActions(c),
        rules: getRuleFields(c.rules).join(", "),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const columnsWithDefaultSort = columns.map((column) => {
    if (column.key.toLowerCase() === sortBy.toLowerCase()) {
      return { ...column, sortDirection: sort === "desc" ? SortDirection.Descend : SortDirection.Ascend };
    }
    return column;
  });

  return (
    <CrmTable
      columns={columnsWithDefaultSort}
      data={groupData}
      pagination={pagination}
      loading={isLoading}
      name="groups"
      onRowClick={(row) => {
        if (accessViewGroup) {
          navigate(`/candidategroups/${row.id}`);
        }
      }}
      isRowDisabled={(_row) => !accessViewGroup}
      rowTitle={viewGroupMessage}
      onSort={onSort}
      order={sort}
      orderBy={sortBy}
      rowTestId={(row) => `group-row-${row.id}`}
      footerActions={
        <ViewArchivedGroups
          isArchivedGroupsOpen={isArchivedGroupsOpen}
          setIsArchivedGroupsOpen={setIsArchivedGroupsOpen}
          sx={{ mt: 1, ml: 1 }}
        />
      }
    />
  );
}
